import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Download, Share2 } from "lucide-react";
import { journeyFeelings, makeJourneyCard } from "../../lib/journeyCard";
import { loadHearts } from "../../lib/hearts";
import { copyText } from "../share/ShareSheet";
import { FlipbookLink } from "../FlipbookLink";
import { FLIPBOOK_ENDING } from "../../lib/copy";
import { RAINBOW_ORDER } from "../../lib/sections";

const quiet =
  "flex items-center gap-2 rounded-full border border-[#7E57C2]/25 bg-white/70 px-4 py-2 font-display text-base text-[#3B2656] transition-colors duration-300 hover:bg-[#7E57C2] hover:text-white disabled:opacity-50";

const COMPACT = "(max-width: 767px), (pointer: coarse)";

function useCompact() {
  const [on, setOn] = useState(() => window.matchMedia(COMPACT).matches);
  useEffect(() => {
    const mq = window.matchMedia(COMPACT);
    const f = () => setOn(mq.matches);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  return on;
}

export function LeaveButton({ onClick }) {
  const compact = useCompact();
  return (
    <button
      data-testid="leave-gallery-button"
      data-compact={compact}
      onClick={onClick}
      className={`fixed z-[44] rounded-full px-4 py-2 font-display text-base italic text-[#5C4A72]/80 transition-colors duration-300 hover:bg-white/60 hover:text-[#2A1B3D] ${
        compact ? "right-4 top-[4.75rem] border border-white/70 bg-white/60 py-1.5 text-sm backdrop-blur-md" : "bottom-4 left-6"
      }`}
    >
      Leave the gallery
    </button>
  );
}

function Shell({ children, onClose, onMemories }) {
  return (
    <motion.div
      data-testid="journey-card"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto bg-[#2A1B3D]/30 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex w-full max-w-md flex-col items-center gap-4 rounded-3xl border border-white/70 bg-[#FBF5FD]/95 p-5 text-center shadow-2xl sm:p-6"
      >
        {children}
        {onMemories && (
          <button data-testid="journey-memories-offer" onClick={onMemories} className="font-display text-base italic text-[#5C4A72] underline-offset-4 transition-colors hover:text-[#2A1B3D] hover:underline">
            Before you go, sit with the memories ›
          </button>
        )}
        <FlipbookLink testid="leave-flipbook-link" className="inline-block py-2 font-display text-base italic text-[#5C4A72] underline-offset-4 transition-colors hover:text-[#2A1B3D] hover:underline">
          {FLIPBOOK_ENDING}
        </FlipbookLink>
      </motion.div>
    </motion.div>
  );
}

const Stay = ({ onClose, text = "Stay a little longer" }) => (
  <button data-testid="journey-stay-button" onClick={onClose} className="font-display text-lg italic text-[#7E57C2] underline-offset-4 transition-colors hover:text-[#2A1B3D] hover:underline">
    {text}
  </button>
);

export function JourneyCard({ paintings, viewed, onClose, onMemories }) {
  const [heartsReady, setHeartsReady] = useState(false);
  const [anyway, setAnyway] = useState(false);
  useEffect(() => {
    loadHearts().catch(() => {}).finally(() => setHeartsReady(true));
  }, []);
  const walked = useMemo(() => (heartsReady ? journeyFeelings(paintings, viewed) : []), [paintings, viewed, heartsReady]);
  // Leaving before stepping in: a farewell card with one feeling from each room, still waiting.
  const waiting = useMemo(() => (anyway ? RAINBOW_ORDER.map((id) => paintings.find((p) => p.section === id)).filter(Boolean).map((p) => ({ emotion: p.emotion, painting: p })) : []), [anyway, paintings]);
  const feelings = walked.length ? walked : waiting;
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const site = `${window.location.origin}/`;

  useEffect(() => {
    if (!feelings.length) return undefined;
    let url = null;
    let live = true;
    makeJourneyCard(feelings, walked.length ? undefined : "the feelings still waiting for you")
      .then(({ blob, info }) => {
        if (!live) return;
        url = URL.createObjectURL(blob);
        setCard({ blob, url, info });
      })
      .catch(() => live && setError("The card couldn't be painted just now."));
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [feelings]); // eslint-disable-line react-hooks/exhaustive-deps

  const file = useMemo(() => card && new File([card.blob], "feeling-heart-journey.png", { type: "image/png" }), [card]);

  if (!feelings.length)
    return (
      <Shell onClose={onClose} onMemories={onMemories}>
        <div data-testid="journey-empty" className="flex flex-col items-center gap-3 px-2 py-6">
          <p className="font-hand text-4xl leading-tight text-[#4A2D55]">You haven't stepped into a painting yet</p>
          <p className="font-display text-base italic text-[#5C4A72]">Walk up to any painting, or tap one, and let it hold you for a moment.</p>
        </div>
        <Stay onClose={onClose} />
        <button data-testid="journey-leave-anyway" onClick={() => setAnyway(true)} className="font-display text-base text-[#5C4A72] underline decoration-[#5C4A72]/30 underline-offset-4 transition-colors hover:text-[#2A1B3D]">
          Leave anyway
        </button>
      </Shell>
    );

  const save = () => {
    const a = document.createElement("a");
    a.href = card.url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setStatus("Card saved");
  };
  const share = async () => {
    if (file && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Feeling Heart · Rainbow by the Rain", text: "The feelings I walked through at Feeling Heart, an exhibition by Priyanka Jha", url: site });
        return setStatus("Shared");
      } catch (e) {
        if (e?.name === "AbortError") return undefined;
      }
    }
    setStatus((await copyText(site)) ? "Link copied" : "Couldn't copy, the link is shown below");
  };

  return (
    <Shell onClose={onClose} onMemories={onMemories}>
      <p className="font-display text-2xl italic text-[#2A1B3D]">Your journey</p>
      <div className="aspect-[1080/1350] w-full max-w-[18rem] overflow-hidden rounded-2xl bg-white/60 shadow-[0_18px_40px_-20px_rgba(91,58,133,0.5)]">
        {card ? (
          <img data-testid="journey-card-preview" src={card.url} alt={`Journey card with ${feelings.length} feelings`} className="h-full w-full object-contain" />
        ) : (
          <div data-testid="journey-card-loading" className="flex h-full items-center justify-center font-hand text-2xl text-[#7C6992]">
            {error || "painting your journey…"}
          </div>
        )}
      </div>
      <ul data-testid="journey-feelings" data-count={feelings.length} className="sr-only">
        {feelings.map((f) => (
          <li key={f.emotion}>{f.emotion}</li>
        ))}
      </ul>
      <div className="flex flex-wrap justify-center gap-2">
        <button data-testid="journey-save-button" disabled={!card} onClick={save} className={quiet}>
          <Download size={13} /> Save
        </button>
        <button data-testid="journey-share-button" disabled={!card} onClick={share} className={quiet}>
          <Share2 size={13} /> Share
        </button>
      </div>
      <Stay onClose={onClose} />
      <p data-testid="journey-status" role="status" className="min-h-[1.25rem] text-xs text-[#7E57C2]">
        {status}
      </p>
      {status.startsWith("Couldn't") && <p className="w-full truncate text-[11px] text-[#7C6992]">{site}</p>}
    </Shell>
  );
}
